import type { CSSProperties } from 'react';
import React, { useRef, useState } from 'react';

import clsx from 'clsx';
import type { DraggableBounds, DraggableData, DraggableEvent } from 'react-draggable';
import Draggable from 'react-draggable';
import { animated, config, useSpring } from 'react-spring';
import { useOverrideGlobalCursor } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

interface StyleProps {
  dragging: boolean;
  maximized: boolean;
  open: boolean;
  position: 'right' | 'left';
}

const width = 8;
const center = width / 2;
const drawerBorderPx = 2;
const positionLeftMargin = `0px 0px 0px -${width}px`;
const positionRightMargin = `0px -${width}px 0px 0px`;

const useStyles = makeStyles<StyleProps>()((theme, { dragging, maximized, open, position }) => ({
  container: {
    display: 'flex',
    flexWrap: 'nowrap',
    overflow: !open ? 'hidden' : undefined,
  },
  draggableDiv: {
    cursor: 'col-resize',
    height: '100%',
    left: position === 'left' ? center : undefined,
    margin: position === 'left' ? positionLeftMargin : positionRightMargin,
    pointerEvents: !open || maximized ? 'none' : undefined,
    /**
     * Draggable div is relatively positioned so as to not take up layout space, be 20px wide
     * and be centered over the border of the right-drawer.
     */
    position: 'relative',
    right: position === 'right' ? center : undefined,
    width,
    // 1 higher than drawer so we can always drag it.
    zIndex: theme.zIndex.drawer + 1,
  },
  drawer: (() => {
    const borderKey: keyof CSSProperties = position === 'left' ? 'borderRight' : 'borderLeft';

    return {
      [borderKey]: dragging
        ? `${drawerBorderPx}px solid ${theme.appColors.primary}`
        : `2px solid ${maximized ? 'transparent' : '#E2E8F0'}`,
      display: 'flex',
      overflow: 'hidden',
      pointerEvents: open ? 'auto' : 'none',
      /**
       * Match the width behavior: snap the divider away when maximizing (the
       * expand reads as a page render), fade it back in when docking.
       */
      transition: maximized ? undefined : 'border-color 0.2s ease',
      zIndex: theme.zIndex.drawer,
    };
  })(),
  drawerContent: {
    flexGrow: 1,
    height: '100%',
    overflowY: 'auto',
    width: '100%',
  },
}));

export interface ExpandableDrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  open: boolean;
  dragBoundsPx?: { left: number; right: number };
  draggable?: boolean;
  drawerContentClassName?: string;
  maximized?: boolean;
  position?: 'right' | 'left';
  width?: number;
}

export function ExpandableDrawer(props: ExpandableDrawerProps): JSX.Element {
  const {
    children,
    className,
    dragBoundsPx,
    draggable = false,
    drawerContentClassName,
    maximized = false,
    open,
    position = 'left',
    width: baseDrawerWidth = 248,
    ...rest
  } = props;

  const draggableRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<boolean>(false);
  const [drawerWidth, setDrawerWidth] = useState(baseDrawerWidth);
  const { classes } = useStyles({ dragging, maximized, open, position });
  const dragAllowancePx = dragBoundsPx ?? { left: 248, right: 0 };
  /**
   * dragBoundsPx.left is how far the drawer may grow beyond its base width;
   * dragBoundsPx.right how far it may shrink below it. Clamping the width (not
   * just the per-gesture handle travel) keeps the limits cumulative across
   * drags — otherwise every re-render hands back a fresh shrink allowance and
   * repeated drags can collapse the drawer entirely.
   */
  const minDrawerWidth = baseDrawerWidth - dragAllowancePx.right;
  const maxDrawerWidth = baseDrawerWidth + dragAllowancePx.left;
  const [bounds, setBounds] = useState<DraggableBounds>({ left: 0, right: 0 });

  useOverrideGlobalCursor(dragging ? 'col-resize' : null);

  /**
   * If the incoming baseDrawerWidth changes (window resize, maximize/dock),
   * adopt it as the drawer width. Synced during render so the new width lands
   * in the same commit.
   */
  const [lastBaseDrawerWidth, setLastBaseDrawerWidth] = useState(baseDrawerWidth);
  if (lastBaseDrawerWidth !== baseDrawerWidth) {
    setLastBaseDrawerWidth(baseDrawerWidth);
    setDrawerWidth(baseDrawerWidth);
  }

  const marginKey: keyof CSSProperties = position === 'left' ? 'marginLeft' : 'marginRight';
  const layoutStyle: CSSProperties = {
    [marginKey]: !open ? -drawerWidth : 0,
    width: drawerWidth,
  };
  const drawerSpringProps = useSpring({
    // Stiff so the drawer tracks the cursor closely while dragging.
    config: config.stiff,
    immediate: !dragging,
    ...layoutStyle,
  });
  /**
   * Dragging the resize handle is the only animated transition. Everything else
   * — open/close, maximize/dock, window resize — snaps, and has to snap in the
   * same paint as the render that caused it, which means going through React
   * rather than the spring: react-spring writes styles from an animation frame,
   * so even an `immediate` spring paints one frame at the previous width.
   *
   * That frame is visible when docking a maximized drawer, because the render
   * that docks it is also the one that reveals another tab (switching away from
   * the chat tab does both). The revealed tab would paint once squeezed against
   * the old maximized width, so anything sizing itself to the space available
   * (see `useResponsiveOverflow`) would lay out for that width and correct
   * itself a frame later.
   */
  const drawerStyle = dragging ? drawerSpringProps : layoutStyle;

  function handleStart(): void {
    // Bound the handle's travel for this gesture by the remaining allowance.
    const growPx = Math.max(0, maxDrawerWidth - drawerWidth);
    const shrinkPx = Math.max(0, drawerWidth - minDrawerWidth);
    setBounds(
      position === 'right' ? { left: -growPx, right: shrinkPx } : { left: -shrinkPx, right: growPx }
    );
    setDragging(true);
  }

  function handleStop(): void {
    setDragging(false);
  }

  function handleDrag(_event: DraggableEvent, data: DraggableData): void {
    const { deltaX } = data;
    const deltaXPos = position === 'left' ? deltaX : -deltaX;
    setDrawerWidth(prevWidth =>
      Math.min(Math.max(prevWidth + deltaXPos, minDrawerWidth), maxDrawerWidth)
    );
  }

  const content = [
    <animated.div key="main-content" className={classes.drawer} style={drawerStyle} {...rest}>
      <div key="children" className={clsx(drawerContentClassName, classes.drawerContent)}>
        {children}
      </div>
    </animated.div>,
  ];
  if (draggable && !maximized) {
    content.push(
      <Draggable
        key="draggable"
        allowAnyClick
        axis="x"
        bounds={bounds}
        nodeRef={draggableRef}
        /** offsetParent necessary so position is reported correctly if drawer is absolutely positioned. */
        offsetParent={document.body}
        position={{ x: 0, y: 0 }}
        onDrag={handleDrag}
        onStart={handleStart}
        onStop={handleStop}
      >
        <div ref={draggableRef} className={classes.draggableDiv} />
      </Draggable>
    );
  }

  return (
    <div className={clsx(className, classes.container)}>
      {position === 'left' ? content : content.reverse()}
    </div>
  );
}
